import {
  Banner,
  Box,
  Input,
  LoadingButton,
  Select,
  Typography,
} from '@oresundsbron/bridge-ui';
import { ArrowRight } from '@oresundsbron/icons';
import {
  toLazyPromise,
  useForm,
  useFormParams,
  useSubscribe,
  ValidationResult,
} from '@oresundsbron/use-form';
import { pipe } from 'fp-ts/lib/function';
import { getOrElseW, map, tryCatch } from 'fp-ts/lib/TaskOption';
import { of } from 'fp-ts/Task';
import { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import { FormButton } from '../Buttons';
import { FieldSet } from '../Fieldset';
import { Data as PaymentMethod } from '../PaymentMethod';
import { Data as PaymentContract } from '../PaymentContract';
import { ContractName } from '../../ContractName';
import { ContractTranslationKey } from '../../../lib/contract-translations';
import { Currency } from '@oresundsbron/type-safeguards';
import { useTranslation } from 'next-i18next';

export type Data = {
  agreement: 'true';
  currency: 'SEK' | 'DKK';
  campaignCode: string;
};

type Labels = Record<
  keyof Data | 'loading' | 'contract' | 'yourOrder' | 'total',
  string
>;

type Props = {
  title: string;
  validateCampaignCode: (
    data: Data
  ) => Promise<ValidationResult<Data> | undefined> | undefined;
  labels: Labels;
  currencyOptions: { label: string; value: string }[];
  helpText: ReactNode;
  paymentProviderText: string;
  contracts: { label: string; value: string }[];
  total: string;
  placeholders: { currency: string };
} & useFormParams<Data>;

export const TermsOfAgreement: FC<Props> = ({
  namespace,
  title,
  currencyOptions,
  labels,
  onValidate,
  defaultValues,
  helpText,
  validateCampaignCode,
  contracts,
  total,
  placeholders,
  paymentProviderText,
}) => {
  const [campaignCode, setCampaignCode] = useState('');
  const [loading, toggleLoading] = useState(false);

  const [agreement, campaignCodeValue, currency] = useSubscribe<Data>(
    namespace,
    `agreement`,
    `campaignCode`,
    'currency'
  );

  const def = useMemo(
    () => ({
      ...defaultValues,
      currency: (currency as Currency) || defaultValues?.currency,
    }),
    [currency, defaultValues]
  );

  const {
    stepNr,
    registerField,
    validate,
    isValid,
    isActive,
    getData,
    setFieldErrors,
    setFieldData,
    resetFieldError,
    getError,
  } = useForm<Data>({
    namespace,
    title,
    onValidate,
    defaultValues: def,
  });

  const [displayInEuro] = useSubscribe<PaymentMethod>(
    'paymentMethod',
    'invoice.displayInEuro'
  );

  const [oresundPay] = useSubscribe<PaymentContract>(
    'paymentAgreement',
    'oresundsPay'
  );

  const { t } = useTranslation(['common'], {
    nsMode: 'fallback',
  });

  const _validateCampaingCode = useCallback(() => {
    if (!campaignCode) {
      return;
    }
    toggleLoading(true);
    return pipe(
      { ...getData(), campaignCode },
      validateCampaignCode,
      (req) => toLazyPromise<ValidationResult<Data> | undefined>(req),
      (req) => tryCatch(req),
      map((err) => {
        if (err !== undefined) {
          setFieldErrors(err);
          return;
        }
        setFieldData('campaignCode', campaignCode, '');
      }),
      map(() => {
        toggleLoading(false);
      }),
      getOrElseW(() => of(undefined))
    )();
  }, [
    setFieldErrors,
    validateCampaignCode,
    getData,
    campaignCode,
    setFieldData,
    toggleLoading,
  ]);

  const isAccepted = useMemo(() => agreement === 'true', [agreement]);

  return (
    <FieldSet title={title} nr={stepNr} id={namespace}>
      {displayInEuro !== 'true' && oresundPay === 'true' ? (
        <Select
          {...registerField('currency')}
          className="col-span-full sm:col-span-4 md:col-span-6"
          label={labels.currency}
          placeholder={placeholders.currency}
          options={currencyOptions}
          disabled={!isActive || !!campaignCodeValue}
        />
      ) : null}
      {oresundPay === 'true' ? (
        <Input
          name="campaignCode"
          onChange={(e) => {
            setCampaignCode(e.target.value);
            resetFieldError('campaignCode');
          }}
          disabled={!isActive || !!campaignCodeValue}
          value={campaignCode}
          className={
            displayInEuro !== 'true'
              ? 'col-span-full sm:col-span-4 sm:col-start-5 md:col-span-6 md:col-start-7'
              : 'col-span-full sm:col-span-4 md:col-span-6'
          }
          label={labels.campaignCode}
          error={getError('campaignCode')}
          endAdornment={
            <LoadingButton
              loadingLabel={labels.loading}
              startIcon={<ArrowRight />}
              isLoading={loading}
              disabled={!isActive || !!campaignCodeValue}
              color="neutral"
              intent="text"
              iconSize="lg"
              className="h-5 w-5"
              onClick={_validateCampaingCode}
            />
          }
        />
      ) : null}
      <Box className="col-span-full mb-6 mt-8">
        <Typography intent="title" className="mb-3">
          {labels.yourOrder}
        </Typography>
        <Typography intent="label" size="sm">
          {labels.contract}
        </Typography>
        <Box as="ul">
          {contracts.map(({ label, value }, i) => (
            <Box key={i} as="li" className="my-3">
              <Box className="flex flex-row justify-between">
                <Typography>
                  <ContractName contract={label as ContractTranslationKey} />
                </Typography>
                <Typography>{value}</Typography>
              </Box>
              <Typography className="text mt-[-4px] text-right text-xs font-light text-primary-800">
                {t('form.labels.exclVAT')}
              </Typography>
            </Box>
          ))}
          <Box
            as="li"
            className="flex flex-row justify-between border-t-2 border-primary-600 py-3"
          >
            <Typography>{labels.total}</Typography>
            <Typography>{total}</Typography>
          </Box>
        </Box>
      </Box>
      {oresundPay !== 'true' ? (
        <Banner className="col-span-full">
          <Typography>{paymentProviderText}</Typography>
        </Banner>
      ) : null}
      <Input
        {...registerField('agreement', { type: 'checkbox', value: 'true' })}
        className="col-span-full"
        label={labels.agreement}
        required
        disabled={!isActive}
      />
      <Typography size="sm" className="col-span-full">
        {helpText}
      </Typography>

      <FormButton
        loadingLabel={labels.loading}
        namespace={namespace}
        onClick={validate}
        disabled={!isActive || !isValid || !isAccepted}
      />
    </FieldSet>
  );
};
